/**
 * Marquee scrolling text element
 * Creates infinite horizontal scrolling animation with configurable speed and pause on hover
 */

export class MarqueeElement extends HTMLElement {
  private animationId: number | null = null
  private scrollPosition: number = 0
  private content: HTMLElement | null = null
  private autoplaySpeed: number = 3000
  private isPaused: boolean = false
  private contentWidth: number = 0
  private resizeObserver: ResizeObserver | null = null
  private originalChildren: Node[] = []
  private isSetUp = false

  /**
   * The animation used to run unconditionally from `connectedCallback` for the life of
   * the page: it wrote `content.style.transform` on EVERY frame, with no visibility
   * gating, and even while `isPaused` it kept re-arming the rAF instead of stopping.
   *
   * Confirmed on an iPhone via Safari Web Inspector's Layers tab: the composited layer
   * wrapping this element had a Paints count climbing past 4,500 and still rising, on a
   * page carrying 135 layers / 341MB of layer memory. Each transform write dirties the
   * layer, so an off-screen marquee was forcing a full-page-width repaint 60x/second
   * indefinitely. That is a permanent GPU and memory-pressure load, which is why the
   * crash was time-based, got worse when scrolling, and was unaffected by removing
   * third-party apps or optimising images (images were only ~18MB).
   *
   * Now the loop only runs while the element is actually on screen, the document is
   * visible, and it is not paused — and it genuinely stops rather than idling.
   */
  private visibilityObserver: IntersectionObserver | null = null
  private isOnScreen = false
  private handleDocumentVisibility = (): void => {
    this.syncAnimationState()
  }

  connectedCallback() {
    // setupMarquee() duplicates its own children to build the seamless loop, so it
    // must run exactly once. Re-running it on reconnect (the element being moved in
    // the DOM, e.g. as a carousel cell) would treat the previous wrapper as original
    // content and clone that too, doubling the subtree on every move.
    if (this.isSetUp) {
      this.calculateContentWidth()
      // `setupResizeObserver` is only reached via `setupMarquee`, so on a reconnect the
      // observer disconnected below was never restored and width recalculation stayed
      // dead for the rest of the page's life.
      this.setupResizeObserver()
    } else {
      this.setupMarquee()
    }

    this.setupVisibilityGate()
    document.addEventListener('visibilitychange', this.handleDocumentVisibility)
    this.syncAnimationState()
  }

  disconnectedCallback() {
    this.stopAnimation()
    this.resizeObserver?.disconnect()
    this.visibilityObserver?.disconnect()
    this.visibilityObserver = null
    this.isOnScreen = false
    document.removeEventListener('visibilitychange', this.handleDocumentVisibility)
  }

  /** Runs the animation only while the marquee is actually visible to the shopper. */
  private setupVisibilityGate() {
    if (!('IntersectionObserver' in window)) {
      // No gate available — fall back to the old always-on behaviour.
      this.isOnScreen = true
      return
    }

    this.visibilityObserver?.disconnect()
    this.visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        this.isOnScreen = entry.isIntersecting
        this.syncAnimationState()
      },
      // A little margin so it is already moving by the time it scrolls into view.
      { rootMargin: '200px' }
    )
    this.visibilityObserver.observe(this)
  }

  /** Single place that decides whether the rAF loop should be running. */
  private syncAnimationState() {
    const shouldRun = this.isOnScreen && !this.isPaused && !document.hidden

    if (shouldRun) {
      this.startAnimation()
    } else {
      this.stopAnimation()
    }
  }

  private setupMarquee() {
    this.isSetUp = true

    // Get autoplay speed from data attribute
    const speedAttr = this.getAttribute('data-autoplay-speed')
    if (speedAttr) {
      this.autoplaySpeed = parseInt(speedAttr, 10) || 3000
    }

    // Store original children
    this.originalChildren = Array.from(this.childNodes)

    // Create content wrapper that will hold original + cloned content
    this.content = document.createElement('div')
    this.content.style.cssText = `
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      min-width: max-content;
    `

    // Move original children to content wrapper
    this.originalChildren.forEach(child => {
      this.content!.appendChild(child)
    })

    // Clone the content for seamless loop
    this.originalChildren.forEach(child => {
      const clone = child.cloneNode(true)
      this.content!.appendChild(clone)
    })

    // Add content to element
    this.appendChild(this.content)

    // Setup resize observer to recalculate content width when content changes
    this.setupResizeObserver()

    // Calculate initial content width after a brief delay to ensure DOM is rendered
    setTimeout(() => this.calculateContentWidth(), 0)

    // Pause on hover
    this.addEventListener('mouseenter', () => this.pause())
    this.addEventListener('mouseleave', () => this.resume())
  }

  private setupResizeObserver() {
    if ('ResizeObserver' in window) {
      this.resizeObserver?.disconnect()
      this.resizeObserver = new ResizeObserver(() => {
        this.calculateContentWidth()
      })

      if (this.content) {
        this.resizeObserver.observe(this.content)
      }
    }
  }

  private calculateContentWidth() {
    if (this.content && this.originalChildren.length > 0) {
      // Calculate width based on original children only (half the total content)
      this.contentWidth = this.content.scrollWidth / 2
    }
  }

  private startAnimation() {
    if (this.animationId) return

    // `will-change` is applied only while actually animating. Left on permanently (as it
    // was in `setupMarquee`) it pins a compositor layer for every marquee on the page
    // whether or not anything is moving.
    if (this.content) this.content.style.willChange = 'transform'

    const animate = () => {
      // Stop, do not idle. Re-arming the rAF while paused kept the loop alive for the
      // life of the page; `syncAnimationState` restarts it when conditions change.
      if (this.isPaused || !this.isOnScreen || document.hidden) {
        this.animationId = null
        if (this.content) this.content.style.willChange = 'auto'
        return
      }

      if (this.content && this.contentWidth > 0) {
        // Calculate speed based on autoplay speed (lower number = faster)
        const speedMultiplier = Math.max(0.2, 3000 / this.autoplaySpeed)
        this.scrollPosition += speedMultiplier
        this.content.style.transform = `translateX(-${this.scrollPosition}px)`

        // Reset position when original content is fully scrolled out
        if (this.scrollPosition >= this.contentWidth) {
          this.scrollPosition = 0
        }
      }

      this.animationId = requestAnimationFrame(animate)
    }

    this.animationId = requestAnimationFrame(animate)
  }

  private stopAnimation() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }

  private pause() {
    this.isPaused = true
    this.syncAnimationState()
  }

  private resume() {
    this.isPaused = false
    this.syncAnimationState()
  }

  // Public methods for external control
  public pauseMarquee() {
    this.pause()
  }

  public resumeMarquee() {
    this.resume()
  }

  public setSpeed(speed: number) {
    this.autoplaySpeed = speed
  }

  public recalculateWidth() {
    this.calculateContentWidth()
  }
}
